class Sidebar {
    private form: HTMLFormElement;
    private filters: NodeListOf<HTMLElement> | undefined;

    constructor(private sidebar: HTMLElement, private type = 'default') {
        this.init();
    }

    private init(): void {
        this.form = this.sidebar.querySelector<HTMLFormElement>('form.product-filters');
        this.filters = this.form?.querySelectorAll<HTMLElement>('.product-filter');

        const summary = this.form?.querySelector<HTMLElement>('.filters-summary');
        const sort = this.form?.querySelector<HTMLElement>('.product-sort');

        if (this.type === 'mobile') {
            this.filters.forEach(filter => {
                if (!filter.classList.contains('filter-category')) {
                    filter.setAttribute('data-active', 'false');
                }
            });
        }

        if (summary) {
            this.formSummary(summary);
        }

        if (sort) {
            this.formSort(sort);
        }

        this.filters?.forEach((filter) => {
            // Filter functions
            this.filterToggle(filter);
            this.filterClear(filter);

            // Filter types
            this.filterPriceSlider(filter);
            this.filterMultiSelect(filter);
            this.filterSelect(filter);
        });
    }

    /** Form functions */

    // Form - Submit
    private formSubmit(path?: string): void {
        const currentPath = window.location.pathname;

        if (path && currentPath !== path) {
            const formData = new FormData(this.form);
            window.location.href = path + '?' + new URLSearchParams(formData as any).toString();
        } else {
            this.form.submit();
        }
    }

    // Form - Clear selection
    private formSummary(summary: HTMLElement): void {
        const applied_filters = summary.querySelectorAll('.summary-applied_filter');

        applied_filters?.forEach((filter: HTMLElement) => {
            const remove_button = filter.querySelector<HTMLButtonElement>('.remove-applied_filter');
            const filter_name = filter.dataset.filterName;
            let related_filter = this.form.querySelector('.product-filter:has(input[name="' + filter_name + '"])');

            if (filter_name === 'price') {
                related_filter = this.form.querySelector('.product-filter:has(input[name="min_price"])') || this.form.querySelector('.product-filter:has(input[name="max_price"])');
            }

            remove_button?.addEventListener('click', () => {
                const url = new URL(window.location.href);

                if (!related_filter) {
                    return;
                }

                if (filter_name === 'price') {
                    url.searchParams.delete('min_price');
                    url.searchParams.delete('max_price');
                } else {
                    const value = url.searchParams.get(filter_name);
                    const values = value ? value.split(',') : [];

                    if (values.length <= 1) {
                        url.searchParams.delete(filter_name);
                    } else {
                        const removeValue = remove_button.getAttribute('data-remove-value');
                        let new_values = values.filter(v => v !== filter.dataset.filterValue);
                        new_values = new_values.filter(v => v !== removeValue);
                        url.searchParams.set(filter_name, new_values.join(','));
                    }
                }

                window.location.href = url.toString();
            });
        })
    }

    // Form - Sort
    private formSort(sort: HTMLElement): void {
        const select = sort.querySelector<HTMLSelectElement>('select');

        if (!select) {
            return;
        }

        select.addEventListener('change', () => {
            this.formSubmit();
        });


        if (this.type === 'mobile' && select.classList.contains('orderby')) {
            const fakeSort = document.querySelector('.filter-mobile select.orderby');

            fakeSort.addEventListener('change', (e) => {
                select.value = (e.target as HTMLSelectElement).value;
                select.dispatchEvent(new Event('change'));
            });
        }
    }

    /** Filter functions */

    // Filter - Toggle opened/closed
    private filterToggle(filter: HTMLElement): void {
        const filter_header = filter.querySelector<HTMLElement>('.filter-header .header-title');

        filter_header.addEventListener('click', () => {
            filter.setAttribute('data-active', filter.getAttribute('data-active') === 'true' ? 'false' : 'true');
        })
    }

    // Filter - Clear selection
    private filterClear(filter: HTMLElement): void {
        const clearButton = filter.querySelector<HTMLButtonElement>('.filter-clear');
        const inputs = filter.querySelectorAll<HTMLInputElement>('input');

        clearButton?.addEventListener('click', () => {
            if (filter.classList.contains('filter-price')) {
                this.resetPriceSlider(filter);
            }

            inputs?.forEach((input) => {
                this.clearInput(input);
                input.dispatchEvent(new Event('change'));
            });

            this.formSubmit();
        });
    }

    // Filter - Multi select
    private filterMultiSelect(filter: HTMLElement): void {
        const inputs = filter.querySelectorAll<HTMLInputElement>('input[type="checkbox"]');
        const inputHidden = filter.querySelector<HTMLInputElement>('input[type="hidden"]');
        let value = inputHidden?.value ? inputHidden?.value.split('|') : [];

        if (!inputHidden) {
            return;
        }

        inputs?.forEach((input) => {
            const inputValue = input?.id;

            input?.addEventListener('change', () => {
                if (input.checked) {
                    value.push(inputValue);
                } else {
                    value = value.filter(v => v !== inputValue);
                }

                inputHidden.value = value.join(',');
                inputHidden.setAttribute('value', inputHidden.value);
                inputHidden.dispatchEvent(new Event('change'));
            });
        });

        let path = window.location.pathname;

        inputHidden.addEventListener('change', debounce(() => {
            if (path !== 'products' && inputs.length <= 1 && inputHidden.dataset.inputTaxonomy === this.form.dataset.currentTaxonomy && !inputHidden.value) {
                path = '/products/';
            }

            this.formSubmit(path);
        }, 300));
    }

    // Filter - Price slider
    private filterPriceSlider(filter: HTMLElement) {
        const price_slider = filter.querySelector<HTMLElement>('.price_slider');

        if (!price_slider) {
            return;
        }

        const inputs = filter.querySelectorAll<HTMLInputElement>('.price_slider_inputs input');

        window.addEventListener('woocommerce_slider_changed', () => {
            this.formSubmit();
        })

        inputs?.forEach(input => {
            const related_hidden = this.form.querySelector<HTMLInputElement>(`input[type="hidden"][name="${input.name}"]`);

            input.addEventListener('blur', () => {
                if (input.name === 'min_price') {
                    const max_input = filter.querySelector<HTMLInputElement>('input[name="max_price"]');
                    if (max_input && input.value && max_input.value && parseFloat(input.value) > parseFloat(max_input.value)) {
                        input.value = max_input.value;
                    } else if (parseFloat(input.min) && (parseFloat(input.value) < parseFloat(input.min))) {
                        input.value = input.min;
                    }
                } else if (input.name === 'max_price') {
                    const min_input = filter.querySelector<HTMLInputElement>('input[name="min_price"]');
                    if (min_input && input.value && min_input.value && parseFloat(input.value) < parseFloat(min_input.value)) {
                        input.value = min_input.value;
                    } else if (parseFloat(input.max) && (parseFloat(input.value) > parseFloat(input.max))) {
                        input.value = input.max;
                    }
                }

                if (related_hidden) {
                    related_hidden.value = input.value;
                    related_hidden.setAttribute('value', input.value);
                    related_hidden.dispatchEvent(new Event('change'));
                }

                this.formSubmit();
            });
        });
    }

    // Filter - Select
    private filterSelect(filter: HTMLElement): void {
        const select = filter.querySelector<HTMLSelectElement>('select');

        if (!select) {
            return;
        }

        select.addEventListener('change', () => {
            this.formSubmit();
        });
    }

    /** Helper functions */

    // Clear input value
    private clearInput(input: HTMLInputElement): void {
        if (input.type === 'checkbox' || input.type === 'radio') {
            input.checked = false;
        } else if (input.type === 'number' && (input.min || input.max)) {
            if (input.dataset.min) {
                input.value = input.min;
            } else if (input.dataset.max) {
                input.value = input.max;
            }
        } else {
            input.value = '';
        }
    }

    // Reset price slider
    private resetPriceSlider(filter: HTMLElement): void {
        const slider = filter.querySelector<HTMLElement>('.price_slider');
        const sliderRange = slider?.querySelector<HTMLElement>('.ui-slider-range');
        const sliderHandleLeft = slider?.querySelector<HTMLElement>('.ui-slider-handle:nth-child(2)');
        const sliderHandleRight = slider?.querySelector<HTMLElement>('.ui-slider-handle:nth-child(3)');

        if (sliderRange && sliderHandleLeft && sliderHandleRight) {
            sliderRange.style.left = '0%';
            sliderRange.style.width = '100%';
            sliderHandleLeft.style.left = '0%';
            sliderHandleRight.style.left = '100%';
        }
    }
}

window.addEventListener('DOMContentLoaded', () => {
    const template = document.querySelector<HTMLTemplateElement>('template.woocommerce-products-sidebar');
    let useType = (template && window.innerWidth <= 767) ? 'mobile' : 'default';
    let instance: Sidebar;

    if (!template) {
        return;
    }

    setSidebar(useType);

    window.addEventListener('resize', debounce(() => {
        const newType = (window.innerWidth <= 767) ? 'mobile' : 'default';

        if (newType === useType) return;
        useType = newType;

        removeCurrentSidebar();

        setSidebar(newType);
    }, 100));

    function setSidebar(type: string) {
        const aside = document.querySelector<HTMLElement>(`aside[data-aside-type="${type}"]`);

        if (!aside) return;

        const clone = template.content.cloneNode(true);
        const classList: string = template.getAttribute('class') || '';

        aside.appendChild(clone);
        aside.setAttribute('class', classList);
        aside.classList.add('woocommerce-products-sidebar-' + type);
        aside.setAttribute('id', 'woocommerce-products-sidebar');

        aside.addEventListener('click', (e) => {
            const target = e.target as HTMLElement;
            if (target && !target.classList.contains('product-filters') && !target.closest('.product-filters')) {
                aside.classList.remove('active');
            }
        });

        instance = new Sidebar(aside, type);
    }

    function removeCurrentSidebar() {
        const aside = document.querySelector<HTMLElement>('aside#woocommerce-products-sidebar');
        if (!aside) return;
        aside.innerHTML = '';
        aside.removeAttribute('class');
        aside.removeAttribute('id');

        instance = null;
    }
});

export function debounce(func: Function, wait: number) {
    let timeout: ReturnType<typeof setTimeout>;

    return function executedFunction(...args: any[]) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };

        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}